/**
 * Jittered exponential backoff for realtime reconnects. Pure: the delay is a
 * function of the previous delay and an injectable random source, so a fleet
 * of players dropped by the same outage spreads its reconnect attempts
 * instead of hammering the gateway in lockstep.
 */

export interface BackoffOptions {
  /** Base delay for the first attempt (ms). */
  min?: number;
  /** Upper bound for any returned delay (ms). */
  max?: number;
  /** Growth multiplier applied to the previous delay. */
  factor?: number;
  /** Jitter width as a fraction of the base delay (0.5 → ±25%). */
  jitter?: number;
  /** Uniform [0, 1) source; injectable for deterministic tests. */
  rand?: () => number;
}

/**
 * Compute the next reconnect delay from the previous one. Pass `prevMs <= 0`
 * for the first attempt (starts at `min`). The result is uniformly jittered
 * around the exponential base and always clamped to `[min, max]`.
 */
export function nextBackoffMs(
  prevMs: number,
  options: BackoffOptions = {},
): number {
  const {
    min = 1_000,
    max = 30_000,
    factor = 2,
    jitter = 0.5,
    rand = Math.random,
  } = options;
  const base = prevMs <= 0 ? min : Math.min(prevMs * factor, max);
  const jittered = base * (1 - jitter / 2 + jitter * rand());
  return Math.round(Math.min(max, Math.max(min, jittered)));
}
